/**
 * Группировка ленты уведомлений по времени для разделителей в дропдауне.
 *
 * Бакеты: Сегодня / Вчера / На этой неделе / На прошлой неделе / Ранее.
 * Грабли, зашитые в логику:
 *  - «Сегодня» и «Вчера» вычитаются ПЕРВЫМИ, и только потом — недели. Иначе в понедельник
 *    «Вчера» (воскресенье) принадлежит прошлой ISO-неделе и задвоилось бы в «На прошлой неделе».
 *  - Все границы — по локальной полуночи (`setHours(0,0,0,0)`) и арифметике дат через `setDate`,
 *    а НЕ по разнице в миллисекундах — иначе переходы на летнее/зимнее время и «утреннее сегодня»
 *    считались бы неверно.
 *  - Неделя начинается с понедельника (ru-локаль).
 */

export type DateBucketKey = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'earlier';

export interface NotificationDateGroup<T> {
    key: DateBucketKey;
    label: string;
    items: T[];
}

const BUCKET_ORDER: DateBucketKey[] = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'earlier'];

const BUCKET_LABEL: Record<DateBucketKey, string> = {
    today: 'Сегодня',
    yesterday: 'Вчера',
    thisWeek: 'На этой неделе',
    lastWeek: 'На прошлой неделе',
    earlier: 'Ранее',
};

function startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d: Date, days: number): Date {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
}

/** Понедельник недели, к которой принадлежит дата (локальная полночь). */
function startOfWeekMonday(d: Date): Date {
    const x = startOfDay(d);
    const dow = (x.getDay() + 6) % 7; // Пн=0 … Вс=6
    x.setDate(x.getDate() - dow);
    return x;
}

/** Бакет даты относительно `now`. Сегодня/Вчера имеют приоритет над недельными границами. */
export function dateBucketOf(date: Date, now: Date): DateBucketKey {
    const t = startOfDay(date).getTime();
    const today = startOfDay(now).getTime();
    if (t >= today) return 'today';

    const yesterday = startOfDay(addDays(now, -1)).getTime();
    if (t === yesterday) return 'yesterday';

    // «На этой неделе» — та же неделя, что и сегодня, но уже без Сегодня/Вчера.
    if (t >= startOfWeekMonday(now).getTime()) return 'thisWeek';

    // «На прошлой неделе» — предыдущая неделя, тоже без уже занятого «Вчера».
    if (t >= startOfWeekMonday(addDays(now, -7)).getTime()) return 'lastWeek';

    return 'earlier';
}

/** Границы бакетов дат — UTC-моменты локальной полуночи; отправляются на бэк для точных счётчиков. */
export interface DateBoundaries {
    /** начало сегодня (локальная полночь) */
    today: Date;
    /** начало вчера */
    yesterday: Date;
    /** понедельник текущей недели */
    weekStart: Date;
    /** понедельник прошлой недели */
    lastWeekStart: Date;
}

/**
 * Вычисляет границы бакетов относительно `now`. Те же `startOfDay`/`startOfWeekMonday`,
 * что и в группировке, — поэтому серверный счётчик по этим границам совпадёт с клиентской
 * раскладкой по группам.
 */
export function computeDateBoundaries(now: Date = new Date()): DateBoundaries {
    return {
        today: startOfDay(now),
        yesterday: startOfDay(addDays(now, -1)),
        weekStart: startOfWeekMonday(now),
        lastWeekStart: startOfWeekMonday(addDays(now, -7)),
    };
}

/**
 * Разбивает уже отсортированный (newest-first) список на непустые группы по времени.
 * Порядок элементов внутри группы сохраняется как во входе.
 */
export function groupNotificationsByDate<T extends { sendedAt: Date | string }>(
    items: T[],
    now: Date = new Date(),
): NotificationDateGroup<T>[] {
    const buckets = new Map<DateBucketKey, T[]>();

    for (const item of items) {
        const key = dateBucketOf(new Date(item.sendedAt), now);
        const arr = buckets.get(key);
        if (arr) {
            arr.push(item);
        } else {
            buckets.set(key, [item]);
        }
    }

    return BUCKET_ORDER.filter((key) => buckets.has(key)).map((key) => ({
        key,
        label: BUCKET_LABEL[key],
        items: buckets.get(key)!,
    }));
}
